/**
 * Schedule service — all Supabase operations for scheduling.
 * Owners: create/edit/delete classes, manage enrollments, check-in students.
 * Students: view classes, enroll, self check-in.
 */

import { supabase } from '../lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ScheduleEvent {
  event_id: string;
  start_time: string;
  end_time: string;
  start_time_local: string | null;
  end_time_local: string | null;
  location_timezone: string | null;
  company_id: string | null;
  class_series_id: string | null;
  sport_name: string | null;
  age_bracket_name: string | null;
  skill_level_name: string | null;
  location_name: string | null;
  instructor_names: string[] | null;
  instructor_ids: string[] | null;
  instructor_avatars: (string | null)[] | null;
  max_capacity: number | null;
  current_registrations: number | null;
  is_special_event: boolean | null;
  event_description: string | null;
}

export interface ClassEnrollment {
  id: string;
  class_id: string;
  student_id: string;
  student_type: 'user' | 'child';
  enrolled_at: string;
  status: string;
  registration_type: string;
  attended: boolean | null;
  checked_in_at: string | null;
  applicant_id: string | null;
  created_by: string | null;
  // Joined fields
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
}

export interface CreateClassInput {
  company_id: string;
  sport_id: string;
  location_id?: string | null;
  age_bracket_id?: string | null;
  skill_level_id?: string | null;
  coach_ids?: string[];
  notes?: string | null;
  start_time: string; // ISO timestamp
  end_time: string;   // ISO timestamp
  event_date: string; // YYYY-MM-DD
  start_time_local?: string; // HH:MM:SS
  end_time_local?: string;   // HH:MM:SS
  max_capacity?: number | null;
  is_special_event?: boolean;
  created_by?: string | null;
}

export interface UpdateClassInput {
  sport_id?: string;
  location_id?: string | null;
  age_bracket_id?: string | null;
  skill_level_id?: string | null;
  coach_ids?: string[];
  notes?: string | null;
  start_time?: string;
  end_time?: string;
  event_date?: string;
  start_time_local?: string;
  end_time_local?: string;
  max_capacity?: number | null;
  is_special_event?: boolean;
}

export interface RecurrenceRule {
  unit: 'day' | 'week' | 'month';
  interval: number;
  /** Lowercase day names: sunday…saturday */
  days?: string[];
}

export interface CreateEventSeriesInput {
  company_id: string;
  sport_id: string;
  location_id: string;
  age_bracket_id?: string | null;
  skill_level_id?: string | null;
  coach_ids?: string[];
  notes?: string | null;
  max_capacity?: number | null;
  /** YYYY-MM-DD */
  start_date: string;
  /** YYYY-MM-DD or null */
  end_date?: string | null;
  /** HH:MM:SS */
  start_time: string;
  /** HH:MM:SS — must not be 00:00:00 */
  end_time: string;
  recurrence_unit: 'day' | 'week' | 'month';
  recurrence_interval: number;
  /** Lowercase day names: sunday…saturday */
  repeat_days?: string[] | null;
  /** IANA timezone string */
  timezone: string;
  is_special_event?: boolean;
  created_by?: string | null;
}

export interface UpdateEventSeriesInput {
  sport_id?: string;
  location_id?: string;
  age_bracket_id?: string | null;
  skill_level_id?: string | null;
  coach_ids?: string[];
  notes?: string | null;
  max_capacity?: number | null;
  start_date?: string;
  end_date?: string | null;
  start_time?: string;
  end_time?: string;
  recurrence_unit?: 'day' | 'week' | 'month';
  recurrence_interval?: number;
  repeat_days?: string[] | null;
  timezone?: string;
  is_active?: boolean;
}

export interface CompanyMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  role: string;
}

export interface CompanyLocation {
  id: string;
  name: string;
  company_id: string | null;
  timezone?: string | null;
}

// ─── Events ──────────────────────────────────────────────────────────────────

export async function fetchScheduleEvents(
  rangeStart: string,
  rangeEnd: string
): Promise<ScheduleEvent[]> {
  const { data, error } = await supabase
    .from('friendly_calendar_events')
    .select('*')
    .gte('start_time', rangeStart)
    .lte('start_time', rangeEnd)
    .order('start_time', { ascending: true });

  if (error) throw new Error(`Failed to fetch events: ${error.message}`);
  return (data || []) as ScheduleEvent[];
}

export async function fetchEventById(eventId: string) {
  const { data, error } = await supabase
    .from('calendar_events')
    .select('*')
    .eq('id', eventId)
    .single();

  if (error) throw new Error(`Failed to fetch event: ${error.message}`);
  return data;
}

export async function createClass(input: CreateClassInput): Promise<string> {
  const { data, error } = await supabase
    .from('calendar_events')
    .insert({
      company_id: input.company_id,
      sport_id: input.sport_id,
      location_id: input.location_id ?? null,
      age_bracket_id: input.age_bracket_id ?? null,
      skill_level_id: input.skill_level_id ?? null,
      coach_ids: input.coach_ids ?? [],
      notes: input.notes ?? null,
      start_time: input.start_time,
      end_time: input.end_time,
      event_date: input.event_date,
      start_time_local: input.start_time_local ?? null,
      end_time_local: input.end_time_local ?? null,
      max_capacity: input.max_capacity ?? null,
      is_special_event: input.is_special_event ?? false,
      created_by: input.created_by ?? null,
    })
    .select('id')
    .single();

  if (error) throw new Error(`Failed to create class: ${error.message}`);
  return data.id;
}

export async function updateClass(eventId: string, input: UpdateClassInput): Promise<void> {
  const { error } = await supabase
    .from('calendar_events')
    .update(input)
    .eq('id', eventId);

  if (error) throw new Error(`Failed to update class: ${error.message}`);
}

/**
 * Delete a single calendar_event via RPC — cascades enrollments and bypasses
 * direct-table RLS edge cases. Mirrors the mobile app's delete path.
 */
export async function deleteClass(eventId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_single_calendar_event', {
    p_event_id: eventId,
  });

  if (error) throw new Error(`Failed to delete class: ${error.message}`);
}

/** Delete an entire event series and every calendar_event linked to it. */
export async function deleteEventSeries(seriesId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_event_series', {
    p_series_id: seriesId,
  });
  if (error) throw new Error(`Failed to delete series: ${error.message}`);
}

/** Delete only future events from a series; past events are preserved. */
export async function deleteFutureEventsFromSeries(seriesId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_future_events_from_series', {
    p_series_id: seriesId,
  });
  if (error) throw new Error(`Failed to delete future events: ${error.message}`);
}

// ─── Event Series (recurring classes) ─────────────────────────────────────────

export async function fetchEventSeriesById(seriesId: string) {
  const { data, error } = await supabase
    .from('event_series')
    .select('*')
    .eq('id', seriesId)
    .single();

  if (error) throw new Error(`Failed to fetch series: ${error.message}`);
  return data;
}

/**
 * Create a recurring class series. A DB trigger populates calendar_events,
 * so no direct insert into calendar_events is needed here.
 */
export async function createEventSeries(input: CreateEventSeriesInput): Promise<string> {
  const payload = {
    company_id: input.company_id,
    sport_id: input.sport_id,
    location_id: input.location_id,
    age_bracket_id: input.age_bracket_id ?? null,
    skill_level_id: input.skill_level_id ?? null,
    coach_ids: input.coach_ids ?? [],
    notes: input.notes ?? null,
    description: input.notes ?? null,
    max_capacity: input.max_capacity ?? null,
    start_date: input.start_date,
    end_date: input.end_date ?? null,
    start_time: input.start_time,
    end_time: input.end_time,
    recurrence_unit: input.recurrence_unit,
    recurrence_interval: input.recurrence_interval,
    repeat_days: input.repeat_days ?? null,
    timezone: input.timezone,
    created_by: input.created_by ?? null,
    // event_series.title is NOT NULL; fallback label mirrors mobile's default
    title: 'Recurring Class',
    // is_active defaults to true so the DB trigger populates calendar_events
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from('event_series')
    .insert(payload)
    .select('id')
    .single();

  if (error) throw new Error(`Failed to create series: ${error.message}`);
  return data.id as string;
}

/**
 * Update an event series and regenerate its future calendar_events so the
 * schedule reflects the new pattern. Past events are preserved by the RPC.
 */
export async function updateEventSeries(
  seriesId: string,
  input: UpdateEventSeriesInput
): Promise<void> {
  const { error } = await supabase
    .from('event_series')
    .update(input)
    .eq('id', seriesId);

  if (error) throw new Error(`Failed to update series: ${error.message}`);

  const { error: regenError } = await supabase.rpc('regenerate_events_for_series', {
    p_series_id: seriesId,
  });
  if (regenError) {
    // Non-fatal: the series row is updated; surface a warning only.
    console.warn('Series updated but event regeneration failed:', regenError);
  }
}

/**
 * Update a single occurrence of a recurring class without modifying the series.
 * Mirrors mobile's "This class only" edit path.
 */
export async function updateSingleOccurrence(params: {
  eventId: string;
  company_id: string | null;
  coach_ids?: string[] | null;
  sport_id?: string | null;
  skill_level_id?: string | null;
  age_bracket_id?: string | null;
  location_id?: string | null;
  max_capacity?: number | null;
  notes?: string | null;
}): Promise<void> {
  // Supabase-generated types mark nullable RPC args as non-nullable strings;
  // nulls are valid at runtime, so we bypass the overly strict narrowing.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).rpc('update_single_calendar_event', {
    p_event_id: params.eventId,
    p_company_id: params.company_id,
    p_coach_ids: params.coach_ids ?? null,
    p_sport_id: params.sport_id ?? null,
    p_skill_level_id: params.skill_level_id ?? null,
    p_age_bracket_id: params.age_bracket_id ?? null,
    p_location_id: params.location_id ?? null,
    p_max_capacity: params.max_capacity ?? null,
    p_notes: params.notes ?? null,
  });

  if (error) throw new Error(`Failed to update occurrence: ${error.message}`);
}

// ─── Enrollments ─────────────────────────────────────────────────────────────

/** My enrollments — for students to know which classes they're in. */
export async function fetchMyEnrollments(userId: string): Promise<ClassEnrollment[]> {
  const { data, error } = await supabase
    .from('class_enrollments')
    .select('*')
    .eq('student_id', userId)
    .eq('student_type', 'user')
    .neq('status', 'cancelled');

  if (error) throw new Error(`Failed to fetch enrollments: ${error.message}`);
  return (data || []) as ClassEnrollment[];
}

/** All enrollments for a class — for owners managing the roster. */
export async function fetchClassEnrollments(classId: string): Promise<ClassEnrollment[]> {
  // Cast to any to avoid TS2589 (excessively deep type instantiation on joined select)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from('class_enrollments')
    .select(`
      id,
      class_id,
      student_id,
      student_type,
      enrolled_at,
      status,
      registration_type,
      attended,
      checked_in_at,
      applicant_id,
      created_by,
      user_profiles!class_enrollments_student_id_fkey (
        first_name,
        last_name,
        email,
        avatar_url
      )
    `)
    .eq('class_id', classId)
    .neq('status', 'cancelled')
    .order('enrolled_at', { ascending: true });

  if (error) throw new Error(`Failed to fetch class enrollments: ${error.message}`);

  return ((data || []) as any[]).map((row) => ({
    id: row.id,
    class_id: row.class_id,
    student_id: row.student_id,
    student_type: row.student_type,
    enrolled_at: row.enrolled_at,
    status: row.status,
    registration_type: row.registration_type,
    attended: row.attended,
    checked_in_at: row.checked_in_at,
    applicant_id: row.applicant_id,
    created_by: row.created_by,
    first_name: row.user_profiles?.first_name ?? null,
    last_name: row.user_profiles?.last_name ?? null,
    email: row.user_profiles?.email ?? null,
    avatar_url: row.user_profiles?.avatar_url ?? null,
  }));
}

export async function enrollInClass(
  classId: string,
  userId: string
): Promise<string> {
  const { data, error } = await supabase
    .from('class_enrollments')
    .insert({
      class_id: classId,
      student_id: userId,
      student_type: 'user',
      enrolled_at: new Date().toISOString(),
      status: 'active',
      registration_type: 'registered',
    })
    .select('id')
    .single();

  if (error) throw new Error(`Failed to enroll: ${error.message}`);
  return data.id;
}

export async function cancelEnrollment(enrollmentId: string): Promise<void> {
  const { error } = await supabase
    .from('class_enrollments')
    .update({ status: 'cancelled' })
    .eq('id', enrollmentId);

  if (error) throw new Error(`Failed to cancel enrollment: ${error.message}`);
}

export async function checkIn(enrollmentId: string): Promise<void> {
  const { error } = await supabase
    .from('class_enrollments')
    .update({ attended: true, checked_in_at: new Date().toISOString() })
    .eq('id', enrollmentId);

  if (error) throw new Error(`Failed to check in: ${error.message}`);
}

// ─── Company Resources ────────────────────────────────────────────────────────

export async function fetchCompanyLocations(companyId: string): Promise<CompanyLocation[]> {
  const { data, error } = await supabase
    .from('locations')
    .select('id, name, company_id, timezone')
    .eq('company_id', companyId)
    .order('name');

  if (error) throw new Error(`Failed to fetch locations: ${error.message}`);
  return (data || []) as CompanyLocation[];
}

export async function fetchCompanyMembers(companyId: string): Promise<CompanyMember[]> {
  const { data, error } = await supabase
    .from('user_company_relationships')
    .select(`
      user_id,
      system_roles ( role ),
      user_profiles!user_company_relationships_user_id_fkey (
        id, first_name, last_name, email, avatar_url
      )
    `)
    .eq('company_id', companyId);

  if (error) throw new Error(`Failed to fetch company members: ${error.message}`);

  return ((data || []) as any[])
    .filter((r) => r.user_profiles)
    .map((r) => ({
      id: r.user_profiles.id,
      first_name: r.user_profiles.first_name,
      last_name: r.user_profiles.last_name,
      email: r.user_profiles.email,
      avatar_url: r.user_profiles.avatar_url,
      role: r.system_roles?.role ?? 'member',
    }));
}
